import React from 'react';
import Box from '@mui/material/Box';
import { Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import StepShell, { PrimaryBtn } from '../StepShell';
import { QuizAnswer } from '../types';
import _ from 'lodash';
import { VALID_QUANTITIES } from './Quantity';

interface Props {
  state: Partial<QuizAnswer>;
  setState: (a: Partial<QuizAnswer>) => void;
  onNext: () => void;
  onBack?: (() => void) | null;
  stepIdx: number;
  total: number;
}

export default function QuantityByMealType({
  state,
  setState,
  onNext,
  onBack,
  stepIdx,
  total,
}: Props) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery<Theme>((t) => t.breakpoints.down('sm'));
  const qty = state?.mealQuantityByMealType
    ? _.sum(Object.values(state?.mealQuantityByMealType))
    : 0;

  const isQuantityValid = (VALID_QUANTITIES as readonly number[]).includes(qty);

  const totalsHint = VALID_QUANTITIES.join(', ').replace(
    /, ([^,]*)$/,
    ' or $1',
  );

  const setQuantityByMealType = (
    key: 'breakfast' | 'lunch' | 'dinner',
    delta: number,
  ) => {
    const qtyByMealType = state?.mealQuantityByMealType || {
      breakfast: 0,
      lunch: 0,
      dinner: 0,
    };
    const newQtyByMealType = {
      ...qtyByMealType,
      [key]: Math.max(0, (qtyByMealType?.[key] || 0) + delta),
    };
    setState({ ...state, mealQuantityByMealType: newQtyByMealType });
  };

  return (
    <StepShell
      stepIdx={stepIdx}
      total={total}
      mobile={isSmallScreen}
      onBack={onBack}
      footer={
        <PrimaryBtn onClick={onNext} disabled={!isQuantityValid}>
          Continue
        </PrimaryBtn>
      }
    >
      <Box sx={{ pt: isSmallScreen ? '4px' : '8px', pb: '16px' }}>
        <Box
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: isSmallScreen ? 30 : 40,
            lineHeight: 1.08,
            letterSpacing: '-0.015em',
            m: 0,
            mb: '6px',
            color: '#041C2C',
            textWrap: 'balance',
          }}
        >
          How many of each?
        </Box>
        <Typography
          variant={'subtitle1'}
          sx={{
            fontSize: isSmallScreen ? 15 : 17,
            m: 0,
            mb: isSmallScreen ? '22px' : '28px',
          }}
        >
          Choose how many breakfasts, lunches, and dinners. Total must be 6–12
          meals.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: '14px 16px',
            borderRadius: '12px',
            background: isQuantityValid ? 'rgba(4,28,44,0.04)' : '#FFFFFF',
            border: isQuantityValid
              ? `1.5px solid ${theme.palette.primary.main}`
              : '1.5px solid #E5E5EE',
            mb: '14px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Box
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: theme.palette.text.secondary,
              }}
            >
              Total
            </Box>
            <Box
              component="span"
              aria-live="polite"
              sx={{
                fontFamily: 'Fraunces, serif',
                fontWeight: 600,
                fontSize: 24,
                lineHeight: 1,
                letterSpacing: '-0.015em',
                color: '#041C2C',
              }}
            >
              {qty} {qty === 1 ? 'meal' : 'meals'}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {isQuantityValid ? (
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.palette.primary.contrastText,
                  background: theme.palette.primary.main,
                  px: '10px',
                  py: '4px',
                  borderRadius: 999,
                }}
              >
                Valid plan size
              </Box>
            ) : (
              <Box
                component="span"
                sx={{ fontSize: 12, color: theme.palette.text.secondary }}
              >
                Pick {totalsHint}
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {(['breakfast', 'lunch', 'dinner'] as const).map((key) => (
            <Stepper
              key={key}
              label={key.charAt(0).toUpperCase() + key.slice(1)}
              value={state?.mealQuantityByMealType?.[key] || 0}
              onDec={() => setQuantityByMealType(key, -1)}
              onInc={() => setQuantityByMealType(key, +1)}
            />
          ))}
        </Box>
      </Box>
    </StepShell>
  );
}

interface StepperProps {
  label: string;
  value: number;
  onDec: () => void;
  onInc: () => void;
}

function Stepper({ label, value, onDec, onInc }: StepperProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: '14px 16px',
        background: '#FFFFFF',
        border: '1.5px solid #E5E5EE',
        borderRadius: '12px',
      }}
    >
      <Box
        component="span"
        sx={{ fontWeight: 600, fontSize: 16, color: '#041C2C' }}
      >
        {label}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Box
          component="button"
          onClick={onDec}
          aria-label={`Decrease ${label}`}
          disabled={value === 0}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 999,
            border: '1.5px solid #E5E5EE',
            background: '#FFFFFF',
            color: value === 0 ? '#C5C5D0' : '#041C2C',
            fontSize: 18,
            fontWeight: 600,
            cursor: value === 0 ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          −
        </Box>
        <Box
          component="span"
          aria-live="polite"
          sx={{
            fontFamily: 'Fraunces, serif',
            fontWeight: 600,
            fontSize: 22,
            minWidth: 24,
            textAlign: 'center',
            letterSpacing: '-0.01em',
            color: '#041C2C',
          }}
        >
          {value}
        </Box>
        <Box
          component="button"
          onClick={onInc}
          aria-label={`Increase ${label}`}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 999,
            border: '1.5px solid #E5E5EE',
            background: '#FFFFFF',
            color: '#041C2C',
            fontSize: 18,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          +
        </Box>
      </Box>
    </Box>
  );
}
